import { useEffect, useMemo } from "react";
import { Navigate, useParams } from "react-router-dom";
import { EXPERIMENTS, getExperiment } from "@/data/experiments";
import { SITE, url } from "@/data/site";
import PageShell from "@/components/PageShell";
import { ExperimentBody, ExperimentHero } from "@/components/experiment/ExperimentSections";
import { track, EVENTS } from "@/lib/analytics";

const buildJsonLd = (experiment, canonical) => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "CreativeWork",
      name: experiment.title,
      url: canonical,
      image: experiment.image,
      description: experiment.seoDescription,
      creativeWorkStatus: "Experiment / R&D",
      creator: { "@id": `${SITE.base}/#organization` },
      keywords: experiment.tech.join(", "),
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Elentara", item: url("/") },
        { "@type": "ListItem", position: 2, name: "Experiments", item: url("/experiments") },
        { "@type": "ListItem", position: 3, name: experiment.title, item: canonical },
      ],
    },
  ],
});

export default function ExperimentDetail() {
  const { slug } = useParams();
  const experiment = getExperiment(slug);

  useEffect(() => {
    if (experiment) track(EVENTS.EXPERIMENT_VIEW, { experiment: experiment.slug });
  }, [experiment]);

  const seo = useMemo(() => {
    if (!experiment) return null;
    const canonical = url(`/experiments/${experiment.slug}`);
    return {
      title: experiment.seoTitle,
      description: experiment.seoDescription,
      canonical,
      image: experiment.image,
      jsonLd: buildJsonLd(experiment, canonical),
    };
  }, [experiment]);

  if (!experiment) return <Navigate to="/experiments" replace />;

  const i = EXPERIMENTS.findIndex((e) => e.slug === experiment.slug);
  const next = EXPERIMENTS[(i + 1) % EXPERIMENTS.length];

  return (
    <PageShell
      testId="experiment-detail-page"
      finalCtaFrom={`experiment:${experiment.slug}`}
      seo={seo}
    >
      <ExperimentHero experiment={experiment} />
      <ExperimentBody experiment={experiment} next={next} />
    </PageShell>
  );
}
